<template>
  <div class="sec-pages">
    <APageHead title="페이지 권한 관리" sub="역할 및 사용자별 페이지 접근 권한을 체계적으로 관리합니다.">
      <template #actions>
        <ABtn
          v-if="activeTab === 'roles'"
          kind="yellow"
          icon="add"
          @click="openRoleDialog"
        >새 역할</ABtn>
        <ABtn
          v-if="activeTab === 'pages'"
          kind="yellow"
          icon="add"
          @click="openPageDialog"
        >새 페이지</ABtn>
      </template>
    </APageHead>

    <div class="sec-pages__stats">
      <ACard class="sec-pages__stat">
        <div class="sec-pages__stat-row">
          <AStat label="전체 역할" :value="String(roles.length)" />
          <div class="sec-pages__stat-icon">
            <i class="material-icons-round">badge</i>
          </div>
        </div>
      </ACard>
      <ACard class="sec-pages__stat">
        <div class="sec-pages__stat-row">
          <AStat label="등록된 페이지" :value="String(pages.length)" />
          <div class="sec-pages__stat-icon">
            <i class="material-icons-round">description</i>
          </div>
        </div>
      </ACard>
      <ACard class="sec-pages__stat">
        <div class="sec-pages__stat-row">
          <AStat label="페이지 카테고리" :value="String(categories.length)" />
          <div class="sec-pages__stat-icon">
            <i class="material-icons-round">folder</i>
          </div>
        </div>
      </ACard>
    </div>

    <ACard body-flush>
      <div class="p-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="p-tabs__item"
          :class="{ 'is-active': activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i class="material-icons-round">{{ tab.icon }}</i>
          {{ tab.label }}
        </button>
      </div>

      <div class="sec-pages__pane">
        <!-- Tab 1: Permissions -->
        <div v-if="activeTab === 'permissions'">
          <div class="sec-pages__pane-head">
            <div>
              <h5 class="sec-pages__h">역할별 페이지 접근 권한 설정</h5>
              <p class="sec-pages__sub">역할을 선택하고 페이지 접근 권한을 관리하세요</p>
            </div>
          </div>

          <div class="p-form-grid p-form-grid--2 sec-pages__filters">
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">account_circle</i>
                역할 선택
              </label>
              <select v-model="selectedRoleIdx" class="p-input" @change="loadRolePermissionsData">
                <option value="">역할을 선택하세요</option>
                <option v-for="role in roles" :key="role.role_idx" :value="role.role_idx">
                  {{ role.role_name }} ({{ role.role_code }})
                </option>
              </select>
            </div>
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">filter_alt</i>
                카테고리 필터
              </label>
              <select v-model="filterCategory" class="p-input">
                <option value="">전체 카테고리</option>
                <option v-for="category in categories" :key="category" :value="category">{{ category }}</option>
              </select>
            </div>
          </div>

          <ATable
            v-if="selectedRoleIdx"
            :columns="permissionCols"
            :rows="filteredPages"
            row-key="page_idx"
          >
            <template #cell:page_category="{ row }">
              <APill kind="default">{{ row.page_category }}</APill>
            </template>
            <template #cell:page_uri="{ row }">
              <code class="sec-pages__uri">{{ row.page_uri }}</code>
            </template>
            <template #cell:page_name="{ row }">
              <strong>{{ row.page_name }}</strong>
            </template>
            <template #cell:permission_code="{ row }">
              <APill kind="info">{{ row.permission_code || '-' }}</APill>
            </template>
            <template #cell:access="{ row }">
              <label
                class="p-switch"
                :class="{ 'is-on': pagePermissions[row.page_idx] }"
                @click="togglePermission(row.page_idx)"
              />
            </template>
            <template #empty>표시할 페이지가 없습니다.</template>
          </ATable>

          <AEmpty
            v-else
            title="역할을 선택하여 권한을 관리하세요"
            sub="위에서 역할을 선택하면 해당 역할의 페이지 접근 권한을 설정할 수 있습니다."
          />

          <div v-if="selectedRoleIdx" class="sec-pages__pane-foot">
            <ABtn
              kind="yellow"
              icon="save"
              :disabled="!hasModifications"
              @click="savePermissions"
            >
              권한 저장
              <span v-if="hasModifications" class="sec-pages__mod-badge">{{ modifiedPermissions.size }}</span>
            </ABtn>
          </div>
        </div>

        <!-- Tab 2: User-specific permissions -->
        <div v-else-if="activeTab === 'users'">
          <div class="sec-pages__pane-head">
            <div>
              <h5 class="sec-pages__h">사용자별 페이지 접근 권한 설정</h5>
              <p class="sec-pages__sub">역할 권한에 더해 특정 사용자에게만 페이지 접근을 추가로 허용하거나 차단합니다</p>
            </div>
          </div>

          <div class="p-form-grid p-form-grid--2 sec-pages__filters">
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">person_search</i>
                사용자 검색
              </label>
              <div class="sec-pages__search">
                <input
                  v-model="userSearchKeyword"
                  class="p-input"
                  placeholder="이름 또는 이메일로 검색"
                  @keyup.enter="searchEmployees"
                />
                <ABtn kind="ghost" icon="search" :disabled="userSearchLoading" @click="searchEmployees">검색</ABtn>
              </div>
            </div>
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">account_circle</i>
                사용자 선택
              </label>
              <select v-model="selectedEmployeeIdx" class="p-input" @change="loadUserPermissionsData">
                <option value="">
                  {{ userSearchResults.length ? '사용자를 선택하세요' : '먼저 사용자를 검색하세요' }}
                </option>
                <option v-for="emp in userSearchResults" :key="emp.idx" :value="emp.idx">
                  {{ emp.name }} ({{ emp.email }})
                </option>
              </select>
            </div>
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">filter_alt</i>
                카테고리 필터
              </label>
              <select v-model="filterCategory" class="p-input">
                <option value="">전체 카테고리</option>
                <option v-for="category in categories" :key="category" :value="category">{{ category }}</option>
              </select>
            </div>
          </div>

          <div v-if="selectedEmployee" class="sec-pages__user-bar">
            <div class="sec-pages__user-info">
              <i class="material-icons-round">account_circle</i>
              <strong>{{ selectedEmployee.name }}</strong>
              <span class="sec-pages__muted">{{ selectedEmployee.email }}</span>
              <APill kind="dark">{{ selectedEmployeeRoleName }}</APill>
            </div>
            <APill v-if="userOverrideCount > 0" kind="info">개별 설정 {{ userOverrideCount }}건</APill>
          </div>

          <ATable
            v-if="selectedEmployee"
            :columns="userPermCols"
            :rows="filteredPages"
            row-key="page_idx"
          >
            <template #cell:page_category="{ row }">
              <APill kind="default">{{ row.page_category }}</APill>
            </template>
            <template #cell:page_uri="{ row }">
              <code class="sec-pages__uri">{{ row.page_uri }}</code>
            </template>
            <template #cell:page_name="{ row }">
              <strong>{{ row.page_name }}</strong>
            </template>
            <template #cell:role_access="{ row }">
              <APill :kind="roleBaseline[row.page_idx] ? 'ok' : 'default'" dot>
                {{ roleBaseline[row.page_idx] ? '허용' : '차단' }}
              </APill>
            </template>
            <template #cell:override="{ row }">
              <select
                class="p-input sec-pages__override"
                :class="{ 'is-overridden': userOverrides[row.page_idx] }"
                :value="userOverrides[row.page_idx] || ''"
                @change="setUserOverride(row.page_idx, $event.target.value)"
              >
                <option value="">역할 기본값</option>
                <option value="Y">허용</option>
                <option value="N">차단</option>
              </select>
            </template>
            <template #cell:effective="{ row }">
              <APill :kind="effectiveAccess(row.page_idx) ? 'ok' : 'err'" dot>
                {{ effectiveAccess(row.page_idx) ? '허용' : '차단' }}
              </APill>
            </template>
            <template #empty>표시할 페이지가 없습니다.</template>
          </ATable>

          <AEmpty
            v-else
            title="사용자를 선택하여 개별 권한을 관리하세요"
            sub="사용자를 검색·선택하면 역할 기본 권한과 별개로 해당 사용자만의 접근 권한을 설정할 수 있습니다."
          />

          <div v-if="selectedEmployee" class="sec-pages__pane-foot">
            <ABtn
              kind="yellow"
              icon="save"
              :disabled="!hasUserModifications"
              @click="saveUserPermissions"
            >
              권한 저장
              <span v-if="hasUserModifications" class="sec-pages__mod-badge">{{ modifiedUserPages.size }}</span>
            </ABtn>
          </div>
        </div>

        <!-- Tab 3: Roles -->
        <div v-else-if="activeTab === 'roles'">
          <div class="sec-pages__pane-head">
            <div>
              <h5 class="sec-pages__h">시스템 역할 관리</h5>
              <p class="sec-pages__sub">역할을 생성하고 수정하세요</p>
            </div>
          </div>

          <ATable :columns="roleCols" :rows="roles" row-key="role_idx">
            <template #cell:role_code="{ row }">
              <APill kind="dark">{{ row.role_code }}</APill>
            </template>
            <template #cell:role_name="{ row }">
              <strong>{{ row.role_name }}</strong>
            </template>
            <template #cell:description="{ row }">
              <span class="sec-pages__muted">{{ row.description || '-' }}</span>
            </template>
            <template #cell:is_active="{ row }">
              <APill :kind="row.is_active === 'Y' ? 'ok' : 'default'" dot>
                {{ row.is_active === 'Y' ? '활성' : '비활성' }}
              </APill>
            </template>
            <template #cell:actions="{ row }">
              <ABtn kind="ghost" size="sm" icon="edit" icon-only @click="editRole(row)" />
            </template>
          </ATable>
        </div>

        <!-- Tab 3: Pages -->
        <div v-else-if="activeTab === 'pages'">
          <div class="sec-pages__pane-head">
            <div>
              <h5 class="sec-pages__h">등록된 페이지 목록</h5>
              <p class="sec-pages__sub">시스템에 등록된 모든 페이지를 확인하고 수정하세요</p>
            </div>
          </div>

          <ATable :columns="pageCols" :rows="pages" row-key="page_idx">
            <template #cell:page_category="{ row }">
              <APill kind="default">{{ row.page_category }}</APill>
            </template>
            <template #cell:page_uri="{ row }">
              <code class="sec-pages__uri">{{ row.page_uri }}</code>
            </template>
            <template #cell:page_name="{ row }">
              <strong>{{ row.page_name }}</strong>
            </template>
            <template #cell:permission_code="{ row }">
              <APill kind="info">{{ row.permission_code || '-' }}</APill>
            </template>
            <template #cell:actions="{ row }">
              <ABtn kind="ghost" size="sm" icon="edit" icon-only @click="editPage(row)" />
            </template>
          </ATable>
        </div>
      </div>
    </ACard>

    <!-- 역할 추가/수정 모달 -->
    <AModal v-model="roleModalOpen" :title="roleEditMode ? '역할 수정' : '새 역할 추가'" size="md">
      <div class="p-form-grid">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">vpn_key</i>
            역할 코드 <span class="p-field__req">*</span>
          </label>
          <input
            v-model="roleForm.role_code"
            class="p-input"
            placeholder="예: admin"
            :disabled="roleEditMode"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">badge</i>
            역할 이름 <span class="p-field__req">*</span>
          </label>
          <input v-model="roleForm.role_name" class="p-input" placeholder="예: 관리자" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">description</i>
            설명
          </label>
          <textarea
            v-model="roleForm.description"
            class="p-input p-input--area"
            placeholder="역할에 대한 설명을 입력하세요"
          />
        </div>
        <div v-if="roleEditMode" class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">toggle_on</i>
            상태
          </label>
          <select v-model="roleForm.is_active" class="p-input">
            <option value="Y">활성</option>
            <option value="N">비활성</option>
          </select>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="roleModalOpen = false">취소</ABtn>
        <ABtn kind="yellow" :icon="roleEditMode ? 'save' : 'check'" @click="submitRole">
          {{ roleEditMode ? '저장' : '추가' }}
        </ABtn>
      </template>
    </AModal>

    <!-- 페이지 추가/수정 모달 -->
    <AModal v-model="pageModalOpen" :title="pageEditMode ? '페이지 수정' : '새 페이지 등록'" size="md">
      <div class="p-form-grid">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">link</i>
            페이지 URI <span class="p-field__req">*</span>
          </label>
          <input v-model="pageForm.page_uri" class="p-input" placeholder="예: /users/managers" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">title</i>
            페이지 이름 <span class="p-field__req">*</span>
          </label>
          <input v-model="pageForm.page_name" class="p-input" placeholder="예: 관리자 목록" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">folder</i>
            카테고리
          </label>
          <input v-model="pageForm.page_category" class="p-input" placeholder="예: users" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">vpn_key</i>
            권한 코드
          </label>
          <input v-model="pageForm.permission_code" class="p-input" placeholder="예: users:view" />
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="pageModalOpen = false">취소</ABtn>
        <ABtn kind="yellow" :icon="pageEditMode ? 'save' : 'check'" @click="submitPage">
          {{ pageEditMode ? '저장' : '추가' }}
        </ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "SystemSecurityPages",
  components: { APageHead, ACard, ABtn, APill, AStat, ATable, AModal, AEmpty },

  data() {
    return {
      selectedRoleIdx: "",
      filterCategory: "",
      pagePermissions: {},
      modifiedPermissions: new Set(),
      activeTab: "permissions",
      tabs: [
        { id: "permissions", label: "권한 매트릭스", icon: "lock" },
        { id: "users",       label: "사용자별 권한", icon: "person" },
        { id: "roles",       label: "역할 관리",     icon: "groups" },
        { id: "pages",       label: "페이지 목록",   icon: "list_alt" },
      ],
      permissionCols: [
        { key: "page_category",    label: "카테고리",  width: 120 },
        { key: "page_uri",         label: "페이지 URI" },
        { key: "page_name",        label: "페이지 이름" },
        { key: "permission_code",  label: "권한 코드", width: 160 },
        { key: "access",           label: "접근 허용", width: 100, align: "center" },
      ],
      // 사용자별 권한 탭
      userSearchKeyword: "",
      userSearchResults: [],
      userSearchLoading: false,
      selectedEmployeeIdx: "",
      selectedEmployee: null,
      roleBaseline: {},      // page_idx → 선택된 사용자의 역할 기본 접근 여부
      userOverrides: {},     // page_idx → '' (역할 기본값) | 'Y' (허용) | 'N' (차단)
      modifiedUserPages: new Set(),
      userPermCols: [
        { key: "page_category", label: "카테고리",  width: 110 },
        { key: "page_uri",      label: "페이지 URI" },
        { key: "page_name",     label: "페이지 이름" },
        { key: "role_access",   label: "역할 기본", width: 90,  align: "center" },
        { key: "override",      label: "개별 설정", width: 130, align: "center" },
        { key: "effective",     label: "최종 접근", width: 90,  align: "center" },
      ],
      roleCols: [
        { key: "role_code",   label: "역할 코드",  width: 140 },
        { key: "role_name",   label: "역할 이름" },
        { key: "description", label: "설명" },
        { key: "is_active",   label: "상태", width: 100, align: "center" },
        { key: "actions",     label: "작업", width: 70,  align: "center" },
      ],
      pageCols: [
        { key: "page_category",   label: "카테고리",  width: 120 },
        { key: "page_uri",        label: "페이지 URI" },
        { key: "page_name",       label: "페이지 이름" },
        { key: "permission_code", label: "권한 코드", width: 160 },
        { key: "actions",         label: "작업", width: 70, align: "center" },
      ],
      roleModalOpen: false,
      roleEditMode: false,
      roleEditingIdx: null,
      roleForm: { role_code: "", role_name: "", description: "", is_active: "Y" },
      pageModalOpen: false,
      pageEditMode: false,
      pageEditingIdx: null,
      pageForm: { page_uri: "", page_name: "", page_category: "", permission_code: "" },
    };
  },

  computed: {
    ...mapState("permissions", ["roles", "pages", "rolePermissions", "userPermissions", "loading"]),
    ...mapGetters("permissions", ["getCategories"]),

    categories() {
      return this.getCategories;
    },

    filteredPages() {
      let filtered = this.pages;
      if (this.filterCategory) {
        filtered = filtered.filter((p) => p.page_category === this.filterCategory);
      }
      return filtered.sort((a, b) => {
        if (a.page_category !== b.page_category) {
          return a.page_category.localeCompare(b.page_category);
        }
        return a.page_uri.localeCompare(b.page_uri);
      });
    },

    hasModifications() {
      return this.modifiedPermissions.size > 0;
    },

    hasUserModifications() {
      return this.modifiedUserPages.size > 0;
    },

    selectedEmployeeRoleName() {
      if (!this.selectedEmployee || !this.selectedEmployee.role_idx) return "역할 없음";
      const role = this.roles.find((r) => r.role_idx === this.selectedEmployee.role_idx);
      return role ? role.role_name : "역할 없음";
    },

    userOverrideCount() {
      return Object.values(this.userOverrides).filter((v) => v === "Y" || v === "N").length;
    },
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    ...mapActions("permissions", [
      "loadRoles",
      "loadPages",
      "loadRolePermissions",
      "updateRolePermissions",
      "loadUserPermissions",
      "updateUserPermissions",
      "createRole",
      "updateRole",
      "createPage",
      "updatePage",
    ]),

    async loadData() {
      try {
        await Promise.all([this.loadRoles(), this.loadPages()]);
        if (this.roles.length > 0 && !this.selectedRoleIdx) {
          this.selectedRoleIdx = this.roles[0].role_idx;
          await this.loadRolePermissionsData();
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        this.$swal({
          icon: "error",
          title: "데이터 로드 실패",
          text: "데이터를 불러오는 중 오류가 발생했습니다.",
        });
      }
    },

    async loadRolePermissionsData() {
      if (!this.selectedRoleIdx) return;

      try {
        await this.loadRolePermissions(this.selectedRoleIdx);
        this.pagePermissions = {};
        this.modifiedPermissions.clear();

        const permissions = this.rolePermissions[this.selectedRoleIdx] || [];
        this.pages.forEach((page) => {
          this.pagePermissions[page.page_idx] = false;
        });
        permissions.forEach((perm) => {
          this.pagePermissions[perm.page_idx] = perm.can_access === "Y";
        });
      } catch (error) {
        console.error("Failed to load role permissions:", error);
      }
    },

    togglePermission(pageIdx) {
      this.pagePermissions[pageIdx] = !this.pagePermissions[pageIdx];
      this.modifiedPermissions.add(pageIdx);
    },

    async searchEmployees() {
      this.userSearchLoading = true;
      try {
        const keyword = this.userSearchKeyword.trim();
        const searchParams = keyword
          ? keyword.includes("@") ? { email: keyword } : { name: keyword }
          : {};
        const res = await this.$axios({
          method: "get",
          url: "/roles/employees",
          params: {
            limit: 50,
            page: 1,
            search_params: JSON.stringify(searchParams),
          },
          loadingBar: true,
        });
        this.userSearchResults = res.data.items || [];
        if (this.userSearchResults.length === 0) {
          this.$swal({
            icon: "info",
            title: "검색 결과 없음",
            text: "조건에 맞는 사용자가 없습니다.",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error("Failed to search employees:", error);
        this.$swal({ icon: "error", title: "검색 실패", text: "사용자 검색 중 오류가 발생했습니다." });
      } finally {
        this.userSearchLoading = false;
      }
    },

    async loadUserPermissionsData() {
      if (!this.selectedEmployeeIdx) {
        this.selectedEmployee = null;
        return;
      }

      try {
        // 직원 상세 조회 (역할 확인용)
        const res = await this.$axios({
          method: "get",
          url: `/roles/employees/${this.selectedEmployeeIdx}`,
          loadingBar: true,
        });
        this.selectedEmployee = {
          idx: this.selectedEmployeeIdx,
          name: res.data.name,
          email: res.data.email,
          role_idx: res.data.role_idx || null,
        };

        // 역할 기본 권한 로드
        this.roleBaseline = {};
        this.pages.forEach((page) => {
          this.roleBaseline[page.page_idx] = false;
        });
        if (this.selectedEmployee.role_idx) {
          await this.loadRolePermissions(this.selectedEmployee.role_idx);
          const rolePerms = this.rolePermissions[this.selectedEmployee.role_idx] || [];
          rolePerms.forEach((perm) => {
            this.roleBaseline[perm.page_idx] = perm.can_access === "Y";
          });
        }

        // 사용자 개별 오버라이드 로드
        // API 미구현/장애 시에도 역할 기본 권한은 계속 표시하도록 별도 처리
        this.userOverrides = {};
        this.modifiedUserPages.clear();
        try {
          await this.loadUserPermissions(this.selectedEmployeeIdx);
          const overrides = this.userPermissions[this.selectedEmployeeIdx] || [];
          overrides.forEach((perm) => {
            this.userOverrides[perm.page_idx] = perm.can_access === "Y" ? "Y" : "N";
          });
        } catch (overrideError) {
          console.error("Failed to load user overrides:", overrideError);
          this.$swal({
            icon: "warning",
            title: "개별 권한 조회 실패",
            html: "사용자 개별 권한 API에 연결할 수 없습니다.<br/>백엔드 API(<code>GET /roles/pagePermission/employees/{idx}/pages</code>)가<br/>아직 배포되지 않았을 수 있습니다.<br/>역할 기본 권한만 표시합니다.",
          });
        }
      } catch (error) {
        console.error("Failed to load user permissions:", error);
        this.$swal({
          icon: "error",
          title: "권한 로드 실패",
          text: "사용자 권한 정보를 불러오는 중 오류가 발생했습니다.",
        });
      }
    },

    setUserOverride(pageIdx, value) {
      if (value) {
        this.userOverrides[pageIdx] = value;
      } else {
        delete this.userOverrides[pageIdx];
      }
      this.modifiedUserPages.add(pageIdx);
    },

    effectiveAccess(pageIdx) {
      const override = this.userOverrides[pageIdx];
      if (override === "Y") return true;
      if (override === "N") return false;
      return !!this.roleBaseline[pageIdx];
    },

    async saveUserPermissions() {
      if (!this.selectedEmployee) return;

      // 오버라이드가 설정된 페이지만 전송 (미포함 페이지는 역할 기본값 적용)
      const pagePermissions = Object.entries(this.userOverrides)
        .filter(([, v]) => v === "Y" || v === "N")
        .map(([pageIdx, canAccess]) => ({
          page_idx: parseInt(pageIdx),
          can_access: canAccess,
        }));

      try {
        await this.updateUserPermissions({
          employeeIdx: this.selectedEmployee.idx,
          pagePermissions,
        });

        this.modifiedUserPages.clear();

        this.$swal({
          icon: "success",
          title: "저장 완료",
          text: `${this.selectedEmployee.name}님의 개별 권한이 저장되었습니다.`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Failed to save user permissions:", error);
        this.$swal({
          icon: "error",
          title: "저장 실패",
          text: error.response?.data?.detail || error.response?.data?.message || "사용자 권한 저장 중 오류가 발생했습니다.",
        });
      }
    },

    async savePermissions() {
      if (!this.selectedRoleIdx) {
        this.$swal({ icon: "warning", title: "역할 미선택", text: "역할을 먼저 선택하세요." });
        return;
      }

      const pagePermissions = Object.entries(this.pagePermissions).map(
        ([pageIdx, canAccess]) => ({
          page_idx: parseInt(pageIdx),
          can_access: canAccess ? "Y" : "N",
        })
      );

      try {
        await this.updateRolePermissions({
          roleIdx: this.selectedRoleIdx,
          pagePermissions,
        });

        this.modifiedPermissions.clear();

        this.$swal({
          icon: "success",
          title: "저장 완료",
          text: "권한이 성공적으로 저장되었습니다.",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Failed to save permissions:", error);

        let errorMessage = "권한 저장 중 오류가 발생했습니다.";
        let errorDetail = "";

        if (error.response && error.response.data) {
          if (error.response.data.detail) {
            errorDetail = error.response.data.detail;
            if (errorDetail.includes('sqlalchemy')) {
              errorMessage = "데이터베이스 오류가 발생했습니다.";
              if (errorDetail.includes('ForeignKeyViolation')) {
                errorMessage = "존재하지 않는 페이지나 역할을 참조하고 있습니다.";
              } else if (errorDetail.includes('IntegrityError')) {
                errorMessage = "데이터 무결성 오류가 발생했습니다.";
              } else if (errorDetail.includes('UniqueViolation')) {
                errorMessage = "중복된 권한 설정이 있습니다.";
              }
            } else {
              errorMessage = errorDetail;
            }
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }

        this.$swal({
          icon: "error",
          title: "저장 실패",
          html: `<p><strong>${errorMessage}</strong></p>${errorDetail ? `<p class="text-sm text-muted mt-2">${errorDetail.substring(0, 200)}${errorDetail.length > 200 ? '...' : ''}</p>` : ''}`,
        });
      }
    },

    openRoleDialog() {
      this.roleEditMode = false;
      this.roleEditingIdx = null;
      this.roleForm = { role_code: "", role_name: "", description: "", is_active: "Y" };
      this.roleModalOpen = true;
    },

    editRole(role) {
      this.roleEditMode = true;
      this.roleEditingIdx = role.role_idx;
      this.roleForm = {
        role_code: role.role_code,
        role_name: role.role_name,
        description: role.description || "",
        is_active: role.is_active || "Y",
      };
      this.roleModalOpen = true;
    },

    async submitRole() {
      if (!this.roleForm.role_code || !this.roleForm.role_name) {
        this.$swal({ icon: "warning", title: "입력 오류", text: "역할 코드와 이름은 필수입니다." });
        return;
      }

      try {
        if (this.roleEditMode) {
          await this.updateRole({
            roleIdx: this.roleEditingIdx,
            roleData: {
              role_name: this.roleForm.role_name,
              description: this.roleForm.description,
              is_active: this.roleForm.is_active,
            },
          });
        } else {
          await this.createRole({
            role_code: this.roleForm.role_code,
            role_name: this.roleForm.role_name,
            description: this.roleForm.description,
          });
        }
        await this.loadRoles();
        this.roleModalOpen = false;
        this.$swal({
          icon: "success",
          title: this.roleEditMode ? "수정 완료" : "추가 완료",
          text: this.roleEditMode ? "역할 정보가 수정되었습니다." : "새 역할이 추가되었습니다.",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: this.roleEditMode ? "수정 실패" : "추가 실패",
          text: "역할 처리 중 오류가 발생했습니다.",
        });
      }
    },

    openPageDialog() {
      this.pageEditMode = false;
      this.pageEditingIdx = null;
      this.pageForm = { page_uri: "", page_name: "", page_category: "", permission_code: "" };
      this.pageModalOpen = true;
    },

    editPage(page) {
      this.pageEditMode = true;
      this.pageEditingIdx = page.page_idx;
      this.pageForm = {
        page_uri: page.page_uri,
        page_name: page.page_name,
        page_category: page.page_category || "",
        permission_code: page.permission_code || "",
      };
      this.pageModalOpen = true;
    },

    async submitPage() {
      if (!this.pageForm.page_uri || !this.pageForm.page_name) {
        this.$swal({ icon: "warning", title: "입력 오류", text: "페이지 URI와 이름은 필수입니다." });
        return;
      }

      try {
        if (this.pageEditMode) {
          await this.updatePage({
            pageIdx: this.pageEditingIdx,
            pageData: { ...this.pageForm },
          });
        } else {
          await this.createPage({ ...this.pageForm });
        }
        await this.loadPages();
        this.pageModalOpen = false;
        this.$swal({
          icon: "success",
          title: this.pageEditMode ? "수정 완료" : "추가 완료",
          text: this.pageEditMode ? "페이지 정보가 수정되었습니다." : "새 페이지가 등록되었습니다.",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        this.$swal({
          icon: "error",
          title: this.pageEditMode ? "수정 실패" : "추가 실패",
          text: "페이지 처리 중 오류가 발생했습니다.",
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.sec-pages {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
  }

  &__stat-icon {
    width: 44px;
    height: 44px;
    border-radius: $p-radius-md;
    background: $p-point-soft;
    color: $p-point-deep;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i { font-size: 22px; }
  }

  &__pane {
    padding: 20px;
  }

  &__pane-head {
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__h {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sub {
    margin: 0;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__filters {
    margin-bottom: 18px;
  }

  &__pane-foot {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }

  &__mod-badge {
    margin-left: 6px;
    padding: 1px 7px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-size: 10px;
    font-weight: 800;
  }

  &__uri {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-point-deep;
    background: $p-point-soft;
    padding: 2px 6px;
    border-radius: 4px;
  }

  &__muted {
    font-size: 12px;
    color: $p-ink-soft;
  }

  &__search {
    display: flex;
    gap: 8px;

    > .p-input { flex: 1; }
  }

  &__user-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-point-soft;
  }

  &__user-info {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    > i {
      font-size: 20px;
      color: $p-point-deep;
    }

    > strong {
      font-size: 13px;
      color: $p-ink;
    }
  }

  &__override {
    width: 110px;
    padding: 4px 8px;
    font-size: 11px;

    &.is-overridden {
      border-color: $p-point-deep;
      color: $p-point-deep;
      font-weight: 700;
    }
  }
}

// p-tabs 패턴 (다른 페이지와 동일)
.p-tabs {
  display: flex;
  border-bottom: 1px solid $p-line;
  padding: 0 12px;
  overflow-x: auto;

  &__item {
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    font-family: $p-font-body;
    font-size: 12px;
    font-weight: 700;
    padding: 12px 18px;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color $p-dur $p-ease, border-color $p-dur $p-ease;
    margin-bottom: -1px;

    > i { font-size: 16px; }

    &:hover { color: $p-ink; }

    &.is-active {
      color: $p-point-deep;
      border-bottom-color: $p-point-deep;
    }
  }
}
</style>
